import React, { useState, useEffect } from "react";
import "react-pro-sidebar/dist/css/styles.css";
import {
  ProSidebar,
  SidebarHeader,
  SidebarContent,
  Menu,
  MenuItem,
} from "react-pro-sidebar";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem as MuiMenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import { authGet, getUser, unwrapRows } from "../../lib/authApi";
import { getActiveDepartment, setActiveDepartment } from "../../lib/departmentSelection";

// Icons
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";

import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

const SIDEBAR_WIDTH = 220;
const SIDEBAR_COLLAPSED = 80;

const Sidebar = ({ onSidebarWidth }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [selected, setSelected] = useState("Lab Results");

  const [isAdmin, setIsAdmin] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(getActiveDepartment()?.id || "");

  useEffect(() => {
    const role = String(getUser()?.role || localStorage.getItem("role") || "").toLowerCase();
    if (["owner", "global_admin", "global_reader", "superadmin", "admin", "reader"].includes(role)) setIsAdmin(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadDepartments() {
      try {
        const orgResp = await authGet("/organizations");
        const orgs = unwrapRows(orgResp);
        const existingOrgId = localStorage.getItem("org_id");
        const org = orgs.find((o) => o.id === existingOrgId) || orgs[0];
        if (!org?.id) return;
        localStorage.setItem("org_id", org.id);

        const deptResp = await authGet(`/departments/active?org_id=${encodeURIComponent(org.id)}`);
        const rows = unwrapRows(deptResp);
        if (cancelled) return;

        setDepartments(rows);

        const current = getActiveDepartment();
        const stillExists = current?.id && rows.some((d) => d.id === current.id);
        if (stillExists) {
          setDepartmentId(current.id);
        } else if (rows.length) {
          setActiveDepartment(rows[0]);
          setDepartmentId(rows[0].id);
        } else {
          setActiveDepartment(null);
          setDepartmentId("");
        }
      } catch (e) {
        if (!cancelled) setDepartments([]);
      }
    }

    loadDepartments();
    window.addEventListener("focus", loadDepartments);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", loadDepartments);
    };
  }, []);

  useEffect(() => {
    if (onSidebarWidth) {
      onSidebarWidth(collapsed && !hovered ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH);
    }
  }, [collapsed, hovered, onSidebarWidth]);

  useEffect(() => {
    const path = location.pathname;

    if (path.startsWith("/lab-results") || path.startsWith("/department-results")) setSelected("Lab Results");
    else if (path.startsWith("/intake") || path.startsWith("/new-test-order")) setSelected("Waiting List");
    else if (path.startsWith("/patients")) setSelected("Patients");
    else if (path.startsWith("/barcode-generator")) setSelected("Barcode Generator");
    else if (path.startsWith("/tracking")) setSelected("Tracking");
    else if (path.startsWith("/education")) setSelected("Education");
    else if (path.startsWith("/admin/accounts")) setSelected("Accounts");
    else if (path.startsWith("/admin")) setSelected("Admin");
  }, [location.pathname]);

  const isCollapsed = collapsed && !hovered;
  const toggleCollapsed = () => setCollapsed(!collapsed);

  const onDepartmentChange = (id) => {
    setDepartmentId(id);
    const dept = departments.find((d) => d.id === id) || null;
    setActiveDepartment(dept);
    if (!location.pathname.startsWith("/lab-results")) navigate("/lab-results");
  };

  const Item = ({ title, to, icon }) => (
    <MenuItem
      active={selected === title}
      icon={icon}
      style={{ color: colors.grey[100] }}
      onClick={() => setSelected(title)}
    >
      <Link to={to} style={{ textDecoration: "none", color: "inherit" }}>
        <Typography>{title}</Typography>
      </Link>
    </MenuItem>
  );

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        alignSelf: "flex-start",
        height: "100vh",
        zIndex: 100,
        transition: "width 0.2s",
        width: isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH,
        "& .pro-sidebar-inner": {
          backgroundColor: `${colors.primary[600]} !important`,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ProSidebar collapsed={isCollapsed} width={isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH}>
        <SidebarHeader>
          <Box
            display="flex"
            alignItems="center"
            justifyContent={isCollapsed ? "center" : "space-between"}
            p={2}
          >
            {!isCollapsed && (
              <Typography variant="subtitle2" color={colors.grey[100]}>
                Equilibrium Diagnostic
              </Typography>
            )}

            <IconButton onClick={toggleCollapsed}>
              {isCollapsed ? (
                <MenuOutlinedIcon sx={{ color: colors.grey[100] }} />
              ) : (
                <ChevronLeftIcon sx={{ color: colors.grey[100] }} />
              )}
            </IconButton>
          </Box>
        </SidebarHeader>

        <SidebarContent>
          <Box px={isCollapsed ? 1 : 2} pb={1}>
            {isCollapsed ? (
              <Box display="flex" justifyContent="center" py={1}>
                <BiotechOutlinedIcon sx={{ color: colors.grey[100] }} />
              </Box>
            ) : (
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel sx={{ color: colors.grey[200] }}>Department</InputLabel>
                <Select
                  label="Department"
                  value={departmentId}
                  onChange={(e) => onDepartmentChange(e.target.value)}
                  sx={{
                    color: colors.grey[100],
                    ".MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[300] },
                    ".MuiSvgIcon-root": { color: colors.grey[100] },
                  }}
                >
                  {departments.length ? (
                    departments.map((d) => (
                      <MuiMenuItem key={d.id} value={d.id}>
                        {d.code} — {d.name}
                      </MuiMenuItem>
                    ))
                  ) : (
                    <MuiMenuItem value="" disabled>No departments enabled</MuiMenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          </Box>

          <Menu iconShape="square">
            <Item title="Lab Results" to="/lab-results" icon={<BiotechOutlinedIcon />} />
            <Item title="Waiting List" to="/intake" icon={<AssignmentTurnedInOutlinedIcon />} />
            <Item title="Patients" to="/patients" icon={<AssignmentIndOutlinedIcon />} />
            <Item title="Barcode Generator" to="/barcode-generator" icon={<QrCode2OutlinedIcon />} />
            <Item title="Tracking" to="/tracking" icon={<LocalShippingOutlinedIcon />} />
            <Item title="Education" to="/education" icon={<MenuBookOutlinedIcon />} />

            {isAdmin && (
              <>
                <Item title="Accounts" to="/admin/accounts" icon={<BusinessOutlinedIcon />} />
                <Item title="Admin" to="/admin" icon={<AdminPanelSettingsOutlinedIcon />} />
              </>
            )}
          </Menu>
        </SidebarContent>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;
